// src/components/inputs/FormInput.jsx

import React, { forwardRef } from 'react';
import {
    InputLabel,
    Typography,
    TextField,
    InputAdornment,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CheckCircle, Error } from '@mui/icons-material';
import i18next from 'i18next';

import PhoneInput from './PhoneInput';
import PasswordInput from './PasswordInput';
import InputFileUpload from './InputFileUpload';
import MultipleSelect from './MultipleSelect';
import DateInput from './DateInput';

import { countries } from '../../constants/phoneCodes';
import SelectAutoComplacent from './SelectAutoComplacent';

const FormInput = forwardRef(
    (
        {
            label,
            placeholder,
            type,
            labelImage,
            labelBioImage,
            selectedCountryCode = countries[0].phone,
            setSelectedCountryCode,
            phoneNumber,
            setPhoneNumber,
            labelBio,
            textareaRows = '150px',
            width = 120,
            borderRadius,
            sx,
            selectedValue,
            handleSelectChange,
            options = [],
            disabled = false,
            name = type,
            onChange,
            value,
            required = true,
            initialMemberIds,
            error,
            helperText,
            height = width === 120 ? 120 : 145,
            isUploading,
            errorMessage,
            successMessage,
            min,
            max,
            inputProps = {},
            fullPhoneNumber,
            disablePast,
            mediaId,
            onDeleted,
            ...rest
        },
        ref
    ) => {
        const theme = useTheme();
        let lng = i18next.language;

        const handleNumberChange = (e) => {
            let value = e.target.value
                .replace(/,/g, '.')
                .replace(/[^0-9.]/g, '');

            const parts = value.split('.');
            if (parts.length > 2) {
                value = parts[0] + '.' + parts.slice(1).join('');
            }

            if (value.startsWith('.')) {
                value = value.substring(1);
            }

            const newEvent = {
                ...e,
                target: {
                    ...e.target,
                    value: value,
                    name: e.target.name,
                },
            };

            if (onChange) {
                onChange(newEvent);
            }
        };

        const validateLink = (value) => {
            const urlRegex = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(:\d+)?(\/[\w-./?%&=]*)?$/;
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return urlRegex.test(value) || emailRegex.test(value);
        };

        return (
            <>
                {label &&
                    <InputLabel
                        htmlFor={label}
                        sx={{ my: 1, fontWeight: 'bold', color: theme.palette.primary.default, textAlign: lng === 'ar' ? 'right' : 'left', direction: lng === 'ar' ? 'rtl' : 'ltr', }}
                    >
                        {label}
                        {required && <span style={{ color: 'red' }}> *</span>}
                    </InputLabel>
                }

                {labelBio && (
                    <InputLabel
                        htmlFor={label}
                        sx={{
                            mb: 1,
                            fontWeight: 'bold',
                            color: theme.palette.secondary.default,
                            fontSize: '0.75rem',
                            textAlign: lng === 'ar' ? 'right' : 'left',
                        }}>
                        {labelBio}
                    </InputLabel>
                )}

                {/* ==== DATE INPUT ==== */}
                {type === 'date' ? (
                    <DateInput
                        name={name}
                        value={value}
                        onChange={onChange}
                        required={required}
                        disabled={disabled}
                        error={!!error}
                        helperText={error || helperText}
                        min={min}
                        max={max}
                        disablePast={disablePast}
                        inputProps={inputProps}
                        {...rest}
                    />
                ) :

                    /* ==== LINK INPUT ==== */
                    type === 'link' ? (
                        <TextField
                            required={required}
                            ref={ref}
                            id={label}
                            placeholder={placeholder}
                            type="text"
                            name={name}
                            value={value}
                            onChange={onChange}
                            fullWidth
                            error={!!error}
                            helperText={error}
                            sx={{
                                backgroundColor: theme.palette.background.input,
                                borderRadius: 1,
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: validateLink(value) ? 'success.main' : 'divider',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: validateLink(value) ? 'success.main' : 'primary.main',
                                    },
                                },
                                ...sx,
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {validateLink(value) && !error ? (
                                            <CheckCircle color="success" />
                                        ) : (
                                            <Error color="error" />
                                        )}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    ) :

                        /* ==== NUMBER INPUT ==== */
                        type === 'number' ? (
                            <input
                                required={required}
                                style={{
                                    backgroundColor: theme.palette.background.input,
                                    ...sx,
                                }}
                                id={label}
                                placeholder={placeholder}
                                type="text"
                                inputMode="decimal"
                                pattern="[0-9]*([.,][0-9]*)?"
                                name={name}
                                value={value}
                                onChange={handleNumberChange}
                                className="input"
                            />
                        ) :

                            /* ==== PHONE INPUT ==== */
                            type === 'tel' ? (
                                <PhoneInput
                                    type={type}
                                    name={name}
                                    selectedCountryCode={selectedCountryCode}
                                    setSelectedCountryCode={setSelectedCountryCode}
                                    phoneNumber={phoneNumber}
                                    setPhoneNumber={setPhoneNumber}
                                    error={error}
                                    required={required}
                                    helperText={helperText}
                                    placeholder={placeholder}
                                    fullPhoneNumber={fullPhoneNumber}
                                />
                            ) :

                                /* ==== PASSWORD INPUT ==== */
                                type === 'password' ? (
                                    <PasswordInput
                                        type={type}
                                        placeholder={placeholder}
                                        onChange={onChange}
                                        name={name}
                                        required={required}
                                    />
                                ) :

                                    /* ==== MESSAGE/TEXTAREA ==== */
                                    type === 'message' || type === 'textarea' ? (
                                        <textarea
                                            required={required}
                                            style={{
                                                backgroundColor: theme.palette.background.input,
                                                height: textareaRows,
                                                padding: '20px',
                                                resize: 'none',
                                            }}
                                            id={label}
                                            multiline={'true'}
                                            rows={6}
                                            placeholder={placeholder}
                                            name={name}
                                            onChange={onChange}
                                            value={value}
                                            className="input"
                                        />
                                    ) :

                                        /* ==== FILE UPLOAD (Single Image) ==== */
                                        type === 'file' ? (
                                            <InputFileUpload
                                                labelImage={labelImage}
                                                labelBioImage={labelBioImage}
                                                width={width}
                                                value={value}
                                                name={name}
                                                height={height}
                                                borderRadius={borderRadius}
                                                onChange={onChange}
                                                required={required}
                                                isUploading={isUploading}
                                                errorMessage={errorMessage}
                                                successMessage={successMessage}
                                                mediaId={mediaId}
                                                onDeleted={onDeleted}
                                            />
                                        ) :

                                            /* ==== SELECT WITH AUTOCOMPLETE ==== */
                                            type === 'select' ? (
                                                <SelectAutoComplacent
                                                    name={name}
                                                    value={value}
                                                    options={options}
                                                    onChange={onChange}
                                                    disabled={disabled}
                                                    sx={sx}
                                                    error={error}
                                                    helperText={helperText}
                                                />
                                            ) :

                                                /* ==== MULTI SELECT ==== */
                                                type === 'multi_select' ? (
                                                    <MultipleSelect
                                                        name={name}
                                                        selectedValue={selectedValue}
                                                        handleSelectChange={handleSelectChange}
                                                        multiOptions={options}
                                                        sx={sx}
                                                    />
                                                ) : (

                                                    /* ==== DEFAULT TEXT INPUT ==== */
                                                    <input
                                                        required={required}
                                                        ref={ref}
                                                        style={{ backgroundColor: theme.palette.background.input, ...sx }}
                                                        id={label}
                                                        placeholder={placeholder}
                                                        type="text"
                                                        name={name}
                                                        onChange={onChange}
                                                        value={value}
                                                        className="input"
                                                        disabled={disabled}
                                                    />
                                                )}

                {error && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                        {error}
                    </Typography>
                )}
            </>
        );
    }
);

export default FormInput;
